import type { PrefetchContext, PrefetchedDataProps } from '#src/shared/prefetch';

import type { MergedState } from '#src/app/shared/hooks/state';
import { useMergedStates } from '#src/app/shared/hooks/state';

import { prefetchDecisionMakerProfilePageDataFromApi, useDecisionMakerProfilePageDataFromApi } from './pageData';
import {
	prefetchDMProfilePagePaginatedResponsesDataFromApi,
	useDMProfilePagePaginatedResponsesDataFromApi,
} from './pageResponsesPaginatedData';
import {
	prefetchDMProfilePagePaginatedTargetedPetitionsDataFromApi,
	useDMProfilePagePaginatedTargetedPetitionsDataFromApi,
} from './pageTargetedPetitionsPaginatedData';

export type DecisionMakerProfilePagePrefetchedContext = Readonly<{
	pageData: NonNullable<Parameters<typeof useDecisionMakerProfilePageDataFromApi>[0]>;
	responsesPaginatedData: NonNullable<Parameters<typeof useDMProfilePagePaginatedResponsesDataFromApi>[0]>;
	targetedPetitionsPaginatedData: NonNullable<
		Parameters<typeof useDMProfilePagePaginatedTargetedPetitionsDataFromApi>[0]
	>;
}>;

export type DecisionMakerProfilePageCombinedState = MergedState<
	ReturnType<typeof useDecisionMakerProfilePageDataFromApi>,
	ReturnType<typeof useDMProfilePagePaginatedResponsesDataFromApi>,
	ReturnType<typeof useDMProfilePagePaginatedTargetedPetitionsDataFromApi>
>;

export function usePageContext(
	slug: string,
	prefetchedData?: DecisionMakerProfilePagePrefetchedContext,
): DecisionMakerProfilePageCombinedState {
	return useMergedStates(
		useDecisionMakerProfilePageDataFromApi(prefetchedData?.pageData, slug),
		useDMProfilePagePaginatedResponsesDataFromApi(prefetchedData?.responsesPaginatedData, slug),
		useDMProfilePagePaginatedTargetedPetitionsDataFromApi(prefetchedData?.targetedPetitionsPaginatedData, slug),
	);
}

export async function prefetchPageContext(
	slug: string,
	context: PrefetchContext,
): Promise<DecisionMakerProfilePagePrefetchedContext> {
	const [pageData, responsesPaginatedData, targetedPetitionsPaginatedData] = await Promise.all([
		prefetchDecisionMakerProfilePageDataFromApi(context, slug),
		prefetchDMProfilePagePaginatedResponsesDataFromApi(context, slug),
		prefetchDMProfilePagePaginatedTargetedPetitionsDataFromApi(context, slug),
	]);

	return { pageData, responsesPaginatedData, targetedPetitionsPaginatedData };
}

export type DecisionMakerProfilePagePrefetchedDataProps =
	PrefetchedDataProps<DecisionMakerProfilePagePrefetchedContext>;
